/** Super-admin: view and adjust one tenant's AI-credit balance.
 *
 * A self-contained card dropped onto the tenant detail page. The adjust
 * form REQUIRES a reason, mirroring the backend — an unexplained balance
 * change is exactly what the ledger exists to prevent, so the UI should
 * not offer one either.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/store";
import type { CreditBalance } from "../../api/store";
import { ApiError } from "../../api/http";
import { Button, Card, Input, Label } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { grouped, tokensFor } from "../../utils/credits";

export function TenantCredits({ tenantId }: { tenantId: string }) {
  const t = useT();
  const { push } = useToast();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [monthly, setMonthly] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingMonthly, setSavingMonthly] = useState(false);

  useEffect(() => {
    api.tenantCredits(tenantId)
      .then((b) => {
        setBalance(b);
        setMonthly(String(b.monthly_credits));
      })
      .catch(() => setBalance(null));
  }, [tenantId]);

  const saveMonthly = async () => {
    const credits = parseInt(monthly, 10);
    if (!Number.isFinite(credits) || credits < 0) return;
    setSavingMonthly(true);
    try {
      const updated = await api.setMonthlyCredits(tenantId, credits);
      setBalance(updated);
      setMonthly(String(updated.monthly_credits));
      push("success", t("admin.credits.monthlySaved"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("admin.credits.failed"));
    } finally {
      setSavingMonthly(false);
    }
  };

  const submit = async () => {
    const credits = parseInt(amount, 10);
    if (!Number.isFinite(credits) || credits === 0 || !reason.trim()) return;
    setBusy(true);
    try {
      const updated = await api.adjustTenantCredits(tenantId, credits, reason.trim());
      setBalance(updated);
      setAmount("");
      setReason("");
      push("success", t("admin.credits.done"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("admin.credits.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold text-ink">{t("admin.credits.title")}</h2>
        <div className="text-right">
          <span className="block text-2xl font-extrabold text-ink">
            {balance ? grouped(balance.balance) : "—"}
          </span>
          {balance && (
            <span className="text-xs text-muted">
              {t("admin.credits.asTokens", {
                tokens: grouped(tokensFor(balance.balance, balance.tokens_per_credit)),
              })}
            </span>
          )}
        </div>
      </div>
      {balance && (
        <p className="mt-1 text-xs text-muted">
          {t("admin.credits.thisMonth", {
            granted: balance.granted_this_month,
            spent: balance.spent_this_month,
          })}
        </p>
      )}

      {/* The recurring allowance. Set once; every month from now on the
          tenant is topped up to this figure automatically. */}
      <div className="mt-4 border-t border-line pt-4">
        <Label htmlFor="credit-monthly">{t("admin.credits.monthly")}</Label>
        <p className="mt-1 text-xs text-muted">{t("admin.credits.monthlyHint")}</p>
        {balance && /^\d+$/.test(monthly) && Number(monthly) > 0 && (
          <p className="mt-1 text-xs font-semibold text-brand-dark">
            {t("admin.credits.monthlyAsTokens", {
              tokens: grouped(tokensFor(Number(monthly), balance.tokens_per_credit)),
            })}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            id="credit-monthly"
            type="number"
            min={0}
            className="w-32"
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
          />
          <Button
            variant="secondary"
            onClick={saveMonthly}
            disabled={savingMonthly || monthly === "" || parseInt(monthly, 10) < 0}
          >
            {t("admin.credits.monthlySave")}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 border-t border-line pt-4 sm:grid-cols-[8rem_1fr]">
        <div className="flex flex-col gap-1">
          <Label htmlFor="credit-amount">{t("admin.credits.amount")}</Label>
          <Input
            id="credit-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="±"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="credit-reason">{t("admin.credits.reason")}</Label>
          <Input
            id="credit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("admin.credits.reasonHint")}
          />
        </div>
      </div>
      <Button
        className="mt-3"
        onClick={submit}
        disabled={busy || !amount || parseInt(amount, 10) === 0 || !reason.trim()}
      >
        {t("admin.credits.apply")}
      </Button>
    </Card>
  );
}
